import { CommaListInputCSV } from '@/pages/settings/CommaListInputCSV.tsx';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, Modal, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { ListResponseDspListItemBean, SettingBean } from 'common/api/api.ts';
import { settingApi } from 'common/api/entitiesApi.tsx';
import { BuildVersions } from 'common/components/BuildVersions.tsx';
import { ListCard } from 'common/components/ListCard.tsx';
import { SearchInput } from 'common/components/SearchInput.tsx';
import { ListFilters, useAppContext } from 'common/context/AppContext.tsx';
import { COLUMN_ID_WIDTH } from 'common/util/constants.ts';
import { initListFilters, useListFilters } from 'common/util/useListFilters.ts'
import { apiCall, camelCaseToWords } from 'common/util/Util.tsx'
import { useEffect, useMemo, useState } from 'react';





const PREVIEW_LINES = 200
const EDITABLE_MAX_CHARS = 2_000_000 // ~2MB

const SettingList = () => {
  const appContext = useAppContext()

  const [filters, setFilters] = useState<ListFilters>(initListFilters())
  const { setFilter } = useListFilters(setFilters)

  const [loading, setLoading] = useState(false)
  const [listData, setListData] = useState<ListResponseDspListItemBean>()

  const [isEditOpen, setEditOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<SettingBean | null>(null)
  const [form] = Form.useForm()

  const isCsvValueType = useMemo(() => editingItem?.valueType === 'csv', [editingItem?.valueType])

  const loadData = () => {
    const size = filters.pageSize ?? 10
    const page = filters.page ?? 1

    apiCall(
      setLoading,
      appContext,
      () =>
        settingApi.settings(filters.sort?.name, filters.sort?.direction, filters.name, {
          query: { size, page },
        }),
      (data) => setListData(data),
    )
  }

  useEffect(() => {
    loadData()
  }, [filters])

  const openEdit = (record: SettingBean) => {
    setEditingItem(record)
    setEditOpen(true)
    form.setFieldsValue({ value: record.value ?? '' })
  }

  const closeEdit = () => {
    setEditOpen(false)
    setEditingItem(null)
    form.resetFields()
  }

  const saveEdit = async () => {
    if (!editingItem || !editingItem.id) return
    const values = await form.validateFields()
    const newValue: string = values.value

    apiCall(
      setLoading,
      appContext,
      () => settingApi.settingEdit({ name: editingItem.name, value: newValue }, editingItem.id as number),
      () => {
        loadData()
        closeEdit()
      },
    )
  }

  const valueRules = () => {
    const vt = editingItem?.valueType
    const isNumber = vt === 'integer' || vt === 'float'
    const isInteger = vt === 'integer'

    return [
      {
        required: isNumber,
        message: 'Please Input Value!',
      },
      isNumber
        ? isInteger
          ? { pattern: /^\d*$/, message: 'Value must be an integer!' }
          : { pattern: /^[\d.]*$/, message: 'Value must be a number!' }
        : {},
    ]
  }

  const downloadCsv = () => {
    const csvText: string = form.getFieldValue('value') ?? ''
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = `${editingItem?.name ?? 'setting'}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()

    URL.revokeObjectURL(url)
  }

  const onUploadCsvFile = async (file: RcFile) => {
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result ?? ''))
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    })

    form.setFieldsValue({ value: text })
  }

  const renderCsvValue = () => {
    return (
      <>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <Upload
            accept=".csv,text/csv"
            showUploadList={false}
            maxCount={1}
            beforeUpload={(file) => {
              void onUploadCsvFile(file)
              return false
            }}
          >
            <Button icon={<UploadOutlined />}>Upload CSV</Button>
          </Upload>

          <Button icon={<DownloadOutlined />} onClick={downloadCsv}>
            Download current CSV
          </Button>
        </div>

        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          CSV columns: <b>countryIso3,creativeType,size,ctr</b>. creativeType: <b>Banner/Video/Native</b>. Native size
          can be empty or "-".
        </Typography.Paragraph>

        <Form.Item shouldUpdate noStyle>
          {() => {
            const v: string = form.getFieldValue('value') ?? ''

            const editable = v.length <= EDITABLE_MAX_CHARS
            if (editable) {
              return (
                <Input.TextArea rows={14} value={v} onChange={(e) => form.setFieldsValue({ value: e.target.value })} />
              )
            }

            // Preview-only mode (large CSV)
            const lines = v.split(/\r?\n/)
            const totalLines = lines.length
            const isTruncated = totalLines > PREVIEW_LINES
            const shown = lines.slice(0, PREVIEW_LINES).join('\n')

            return (
              <>
                <Typography.Text type="warning">
                  CSV is large; editor disabled to avoid UI lag.
                  {isTruncated ? ` Showing first ${PREVIEW_LINES} lines.` : null}
                </Typography.Text>

                <pre
                  style={{
                    marginTop: 8,
                    maxHeight: 420,
                    overflow: 'auto',
                    padding: 12,
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    background: '#fafafa',
                    whiteSpace: 'pre',
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    fontSize: 12,
                    lineHeight: 1.35,
                  }}
                >
                  {shown}
                </pre>
              </>
            )
          }}
        </Form.Item>
      </>
    )
  }

  const renderValueInput = () => {
    const vt = editingItem?.valueType

    if (isCsvValueType) {
      return renderCsvValue()
    }

    if (vt === 'integer' || vt === 'float') {
      return <InputNumber min={0} step={vt === 'integer' ? 1 : 0.01} style={{ width: '100%' }} />
    }
    if (vt === 'list') {
      return <CommaListInputCSV form={form as any} />
    }
    return <Input />
  }

  return (
    <>
      <ListCard<SettingBean>
        loading={loading}
        title="Settings"
        extra={<BuildVersions />}
        filters={filters}
        setFilters={setFilters}
        listData={listData as any}
        loadData={loadData}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            width: COLUMN_ID_WIDTH,
            sorter: true,
          },
          {
            title: 'Name',
            dataIndex: 'name',
            className: 'list-table-name',
            sorter: true,
            ellipsis: true,
            render: (_val, record: SettingBean) => camelCaseToWords(record.name),
          },
          {
            title: 'Description',
            dataIndex: 'description',
            ellipsis: true,
          },
          {
            title: 'Value',
            dataIndex: 'value',
            sorter: true,
            ellipsis: true,
            render: (text: string) => (text && text.length > 32 ? text.substring(0, 32) + '...' : text),
            width: 220,
          },
          {
            title: 'Actions',
            width: 120,
            render: (_, record: SettingBean) => (
              <Typography.Link onClick={() => openEdit(record)} style={{ whiteSpace: 'nowrap' }}>
                Edit
              </Typography.Link>
            ),
          },
        ]}
      >
        <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)} />
      </ListCard>

      <Modal
        open={isEditOpen}
        title={editingItem ? `Edit value: ${editingItem.name}` : 'Edit value'}
        onOk={saveEdit}
        onCancel={closeEdit}
        okText="Save"
        width={isCsvValueType ? 900 : 520}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Value" name="value" rules={valueRules()}>
            {renderValueInput()}
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default SettingList
